import * as React from 'react';
import api from '../api/apiClient';
import moment from 'moment';
import 'moment/locale/is';
import { Nav, Tab } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { withRouter } from '../hoc/withRouter';
import GeneralTab from '../Components/SeriesTabs/GeneralTab';
import DivisionsTab from '../Components/SeriesTabs/DivisionsTab';
import ClassesTab from '../Components/SeriesTabs/ClassesTab';

moment.locale('is');

interface SeriesClass {
	id: string;
	name: string;
	seriesId: string;
	divisionId?: string | null;
	teamSize: number;
	lookupName: string;
	pointsScope: number;
}

interface Division {
	id: string;
	name: string;
	seriesId: string;
	description: string;
}

interface ManageSeriesState {
	// General tab
	seriesId: string;
	name: string;
	start: moment.Moment;
	end: moment.Moment;

	// Divisions tab
	divisions: Division[];
	editingDivision: Division | null;
	isCreatingDivision: boolean;

	// Classes tab
	seriesClasses: SeriesClass[];
	editingClass: SeriesClass | null;
	isCreatingClass: boolean;

	activeTab: string;
}

class ManageSeriesPage extends React.Component<any, ManageSeriesState> {
	constructor(props) {
		super(props);
		this.saveSeries = this.saveSeries.bind(this);
		this.deleteSeries = this.deleteSeries.bind(this);
	}

	state: ManageSeriesState = {
		// General
		seriesId: '',
		name: '',
		start: moment(),
		end: moment(),

		// Divisions
		divisions: [],
		editingDivision: null,
		isCreatingDivision: false,

		// Classes
		seriesClasses: [],
		editingClass: null,
		isCreatingClass: false,

		activeTab: 'general'
	};

	componentDidMount() {
		const seriesId = this.props.match.params.seriesid;
		if (seriesId) {
			this.loadSeries(seriesId);
			this.loadDivisions(seriesId);
			this.loadClasses(seriesId);
		}
	}

	componentDidUpdate(prevProps) {
		const seriesId = this.props.match.params.seriesid;
		if (prevProps.match.params.seriesid !== seriesId) {
			if (seriesId) {
				this.loadSeries(seriesId);
				this.loadDivisions(seriesId);
				this.loadClasses(seriesId);
			} else {
				this.setState({
					seriesId: '',
					name: '',
					start: moment(),
					end: moment(),
					divisions: [],
					seriesClasses: []
				});
			}
		}
	}

	// ========== GENERAL TAB ==========

	loadSeries(id) {
		api.request('series/' + id).then(data => {
			this.setState({
				seriesId: data.id,
				name: data.name,
				start: moment(data.start),
				end: moment(data.end)
			});
		});
	}

	saveSeries() {
		const series = {
			name: this.state.name,
			start: this.state.start,
			end: this.state.end
		};

		if (this.state.seriesId) {
			api.put('series/' + this.state.seriesId, series).then(data => {
				alert('Series updated successfully!');
			});
		} else {
			api.post('series/', series).then(data => {
				this.props.history.push('/');
			});
		}
	}

	deleteSeries() {
		if (confirm('Are you sure you want to delete this series? This will delete all events, races, and competitors in this series.')) {
			api.delete('series/' + this.state.seriesId).then(data => {
				this.props.history.push('/');
			});
		}
	}

	// ========== DIVISIONS TAB ==========

	loadDivisions(id) {
		api.request('divisions?seriesId=' + id).then(data => {
			this.setState({ divisions: data });
		});
	}

	startCreateDivision = () => {
		this.setState({
			isCreatingDivision: true,
			editingDivision: {
				id: '00000000-0000-0000-0000-000000000000',
				name: '',
				seriesId: this.state.seriesId,
				description: ''
			}
		});
	}

	startEditDivision = (division: Division) => {
		this.setState({
			isCreatingDivision: false,
			editingDivision: { ...division }
		});
	}

	cancelEditDivision = () => {
		this.setState({
			editingDivision: null,
			isCreatingDivision: false
		});
	}

	saveDivision = () => {
		const { editingDivision, isCreatingDivision } = this.state;

		if (!editingDivision || !editingDivision.name) {
			alert('Name is required');
			return;
		}

		if (isCreatingDivision) {
			const divisionToSave = {
				name: editingDivision.name,
				seriesId: editingDivision.seriesId,
				description: editingDivision.description || ''
			};

			api.post('divisions', divisionToSave).then(data => {
				this.loadDivisions(this.state.seriesId);
				this.loadClasses(this.state.seriesId);
				this.cancelEditDivision();
			});
		} else {
			api.put('divisions/' + editingDivision.id, editingDivision).then(data => {
				this.loadDivisions(this.state.seriesId);
				this.loadClasses(this.state.seriesId);
				this.cancelEditDivision();
			});
		}
	}

	deleteDivision = (division: Division) => {
		if (confirm('Are you sure you want to delete division "' + division.name + '"? Classes in this division will become unassigned.')) {
			api.delete('divisions/' + division.id).then(data => {
				this.loadDivisions(this.state.seriesId);
				this.loadClasses(this.state.seriesId);
			});
		}
	}

	updateEditingDivision = (field: keyof Division, value: any) => {
		this.setState(prevState => ({
			editingDivision: {
				...prevState.editingDivision!,
				[field]: value
			}
		}));
	}

	// ========== CLASSES TAB ==========

	loadClasses(id) {
		api.request('seriesclasses/?seriesId=' + id).then(data => {
			this.setState({ seriesClasses: data });
		});
	}

	startCreateClass = () => {
		this.setState({
			isCreatingClass: true,
			editingClass: {
				id: '00000000-0000-0000-0000-000000000000',
				name: '',
				seriesId: this.state.seriesId,
				divisionId: null,
				teamSize: 1,
				lookupName: '',
				pointsScope: 0
			}
		});
	}

	startEditClass = (seriesClass: SeriesClass) => {
		this.setState({
			isCreatingClass: false,
			editingClass: { ...seriesClass }
		});
	}

	cancelEditClass = () => {
		this.setState({
			editingClass: null,
			isCreatingClass: false
		});
	}

	saveClass = () => {
		const { editingClass, isCreatingClass } = this.state;

		if (!editingClass || !editingClass.name) {
			alert('Name is required');
			return;
		}

		if (isCreatingClass) {
			const classToSave = {
				name: editingClass.name,
				seriesId: editingClass.seriesId,
				divisionId: editingClass.divisionId,
				teamSize: editingClass.teamSize,
				lookupName: editingClass.lookupName || '',
				pointsScope: editingClass.pointsScope || 0
			};

			api.post('seriesclasses/class', classToSave).then(data => {
				this.loadClasses(this.state.seriesId);
				this.cancelEditClass();
			});
		} else {
			api.put('seriesclasses/class/' + editingClass.id, editingClass).then(data => {
				this.loadClasses(this.state.seriesId);
				this.cancelEditClass();
			});
		}
	}

	deleteClass = (seriesClass: SeriesClass) => {
		if (confirm('Are you sure you want to delete class "' + seriesClass.name + '"?')) {
			api.delete('seriesclasses/class/' + seriesClass.id).then(data => {
				this.loadClasses(this.state.seriesId);
			});
		}
	}

	updateEditingClass = (field: keyof SeriesClass, value: any) => {
		this.setState(prevState => ({
			editingClass: {
				...prevState.editingClass!,
				[field]: value
			}
		}));
	}

	// ========== RENDER ==========

	render() {
		return (
			<div>
				<div className="d-flex justify-content-between align-items-center mb-3">
					<h2 className="mb-0">{this.state.seriesId ? 'Manage Series' : 'Add New Series'}</h2>
					<Link to="/" className="btn btn-secondary">
						<span className="fa fa-arrow-left me-2"></span>
						Back to Home
					</Link>
				</div>

				<Tab.Container
					activeKey={this.state.activeTab}
					onSelect={(k) => this.setState({ activeTab: k || 'general' })}
				>
					<Nav variant="tabs">
						<Nav.Item>
							<Nav.Link eventKey="general">General</Nav.Link>
						</Nav.Item>
						{this.state.seriesId && (
							<>
								<Nav.Item>
									<Nav.Link eventKey="divisions">Divisions</Nav.Link>
								</Nav.Item>
								<Nav.Item>
									<Nav.Link eventKey="classes">Classes</Nav.Link>
								</Nav.Item>
							</>
						)}
					</Nav>

					<Tab.Content>
						<Tab.Pane eventKey="general">
							<GeneralTab
								seriesId={this.state.seriesId}
								name={this.state.name}
								start={this.state.start}
								end={this.state.end}
								onNameChange={(name) => this.setState({ name })}
								onStartChange={(start) => this.setState({ start })}
								onEndChange={(end) => this.setState({ end })}
								onSave={this.saveSeries}
								onDelete={this.deleteSeries}
							/>
						</Tab.Pane>
						{this.state.seriesId && (
							<>
								<Tab.Pane eventKey="divisions">
									<DivisionsTab
										seriesId={this.state.seriesId}
										divisions={this.state.divisions}
										editingDivision={this.state.editingDivision}
										isCreating={this.state.isCreatingDivision}
										onStartCreate={this.startCreateDivision}
										onStartEdit={this.startEditDivision}
										onCancelEdit={this.cancelEditDivision}
										onSave={this.saveDivision}
										onDelete={this.deleteDivision}
										onFieldChange={this.updateEditingDivision}
									/>
								</Tab.Pane>
								<Tab.Pane eventKey="classes">
									<ClassesTab
										seriesId={this.state.seriesId}
										classes={this.state.seriesClasses}
										divisions={this.state.divisions}
										editingClass={this.state.editingClass}
										isCreating={this.state.isCreatingClass}
										onStartCreate={this.startCreateClass}
										onStartEdit={this.startEditClass}
										onCancelEdit={this.cancelEditClass}
										onSave={this.saveClass}
										onDelete={this.deleteClass}
										onFieldChange={this.updateEditingClass}
									/>
								</Tab.Pane>
							</>
						)}
					</Tab.Content>
				</Tab.Container>
			</div>
		);
	}
}

export default withRouter(ManageSeriesPage);
